import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Key, Zap, Droplet, Wind, Square, Shield, Clock, MapPin, CheckCircle, Phone } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import StickyCallButton from '@/components/StickyCallButton';
import PageSEO from '@/components/PageSEO';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [cities, setCities] = useState([]);

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await axios.get(`${API}/cities`);
        setCities(response.data.slice(0, 6));
      } catch (error) {
        console.error('Fehler beim Laden der Städte:', error);
      }
    };
    fetchCities();
  }, []);

  const services = [
    {
      icon: Key,
      title: 'Schlüsseldienst & Türöffnung',
      slug: 'schluesseldienst',
      description: 'Professionelle Türöffnung ohne Schäden. Wir sind in kürzester Zeit vor Ort und öffnen Ihre Tür fachgerecht.',
    },
    {
      icon: Zap,
      title: 'Elektro-Notdienst',
      slug: 'elektro-notdienst',
      description: 'Schnelle Hilfe bei Stromausfällen, defekten Sicherungen und elektrischen Notfällen rund um die Uhr.',
    },
    {
      icon: Droplet,
      title: 'Sanitär & Heizung',
      slug: 'sanitaer-heizung',
      description: 'Sofortmaßnahmen bei Wasserschaden, Rohrbruch oder Heizungsausfall. Wir minimieren Schäden schnellstmöglich.',
    },
    {
      icon: Wind,
      title: 'Rohrreinigung',
      slug: 'rohrreinigung',
      description: 'Professionelle Beseitigung von Verstopfungen in Abflüssen, Toiletten und Rohrleitungen.',
    },
    {
      icon: Square,
      title: 'Glas-Notdienst',
      slug: 'glas-notdienst',
      description: 'Schnelle Notverglasung bei zerbrochenen Fenstern oder Türen. Sicherung Ihres Objekts sofort.',
    },
    {
      icon: Shield,
      title: 'Einbruchschutz',
      slug: 'einbruchschutz',
      description: 'Maßnahmen zur Sicherung nach Einbrüchen und Installation von Sicherheitstechnik.',
    },
  ];

  const features = [
    { icon: Clock, title: '24/7 Verfügbar', description: 'Rund um die Uhr für Sie erreichbar' },
    { icon: MapPin, title: 'Schnell vor Ort', description: 'Kurze Anfahrtszeiten im gesamten Einsatzgebiet' },
    { icon: CheckCircle, title: 'Qualifizierte Fachkräfte', description: 'Erfahrene Handwerker mit Expertise' },
    { icon: Shield, title: 'Zuverlässig', description: 'Transparente Abläufe und professionelle Arbeit' },
  ];

  const faqs = [
    {
      question: 'Wie schnell sind Sie vor Ort?',
      answer: 'Wir bemühen uns, bei Notfällen schnellstmöglich bei Ihnen zu sein. Die Anfahrtszeit hängt von Ihrem Standort und der aktuellen Auftragslage ab. In den meisten Fällen erreichen wir Sie innerhalb von 30-60 Minuten.',
    },
    {
      question: 'Welche Gebiete decken Sie ab?',
      answer: 'Unser Einsatzgebiet umfasst Dortmund und einen Umkreis von ca. 40 km. Dazu gehören unter anderem Bochum, Hagen, Essen, Hamm, Witten, Iserlohn, Gelsenkirchen und Wuppertal sowie viele weitere Städte und Gemeinden im Ruhrgebiet und angrenzenden Regionen.',
    },
    {
      question: 'Sind Sie wirklich 24 Stunden erreichbar?',
      answer: 'Ja, unser Notdienst ist 365 Tage im Jahr rund um die Uhr für Sie erreichbar – auch an Wochenenden, Feiertagen und nachts. Rufen Sie einfach unsere Hotline an: 0231 39083687',
    },
    {
      question: 'Welche Leistungen bieten Sie an?',
      answer: 'Wir bieten ein breites Spektrum an Notdienst-Leistungen: Schlüsseldienst und Türöffnung, Elektro-Notdienst, Sanitär- und Heizungsnotdienst, Rohrreinigung, Glas-Notdienst sowie Einbruchschutz und Notverglasung.',
    },
    {
      question: 'Wie erreiche ich Sie im Notfall?',
      answer: 'Im Notfall erreichen Sie uns jederzeit unter unserer 24/7-Hotline: 0231 39083687. Alternativ können Sie uns auch per E-Mail kontaktieren: info@handwerkernotdienst.nrw',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <PageSEO
        title="Handwerkernotdienst NRW | 24/7 Notdienst im Ruhrgebiet ☎ 0231 39083687"
        description="24/7 Handwerkernotdienst im Ruhrgebiet: Schlüsseldienst, Elektro-Notdienst, Sanitär, Rohrreinigung & mehr. Schnelle Hilfe ☎ 0231 39083687. Zuverlässig & transparent."
        canonicalUrl="https://handwerkernotdienst.nrw/"
        breadcrumbs={[
          { name: 'Start', url: 'https://handwerkernotdienst.nrw/' }
        ]}
      />
      
      <Header />
      <StickyCallButton />

      {/* Hero Section */}
      <section className="hero-section relative bg-gradient-to-br from-[#e8f4f8] via-white to-[#f0f9ff] pt-16 pb-24" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-[#0066cc] text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <Clock className="w-4 h-4" />
              <span>24/7 Notdienst – Immer für Sie da</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight" data-testid="hero-title">
              Schnelle Hilfe in Notfällen<br />
              <span className="text-[#0066cc]">Rund um die Uhr</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Ihr zuverlässiger Handwerkernotdienst im Raum Dortmund und Umgebung. 
              Professionelle Lösungen bei Türöffnungen, Elektro-, Sanitär- und Glasnotfällen.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a href="tel:+4923139083687" data-testid="hero-call-button">
                <Button size="lg" className="bg-[#e63946] hover:bg-[#d62828] text-white text-lg px-8 py-6 rounded-full font-bold shadow-lg">
                  <Phone className="w-6 h-6 mr-2" />
                  Jetzt anrufen: 0231 39083687
                </Button>
              </a>
              <Link to="/kontakt" data-testid="hero-contact-button">
                <Button size="lg" variant="outline" className="text-lg px-8 py-6 rounded-full font-semibold border-2 border-[#0066cc] text-[#0066cc] hover:bg-[#0066cc] hover:text-white">
                  Kontaktformular
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Hero Banner Image */}
        <div className="mt-12 max-w-5xl mx-auto px-4">
          <div className="rounded-2xl overflow-hidden shadow-2xl" data-testid="hero-banner-image">
            <img 
              src="https://customer-assets.emergentagent.com/job_repair-hero-4/artifacts/xsv6bl5n_bunner1.jpg" 
              alt="Handwerkernotdienst NRW - Professionelles Team im Einsatz" 
              className="w-full h-auto object-cover"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="text-center" data-testid={`feature-${index}`}>
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[#e8f4f8] rounded-full mb-4">
                    <Icon className="w-8 h-8 text-[#0066cc]" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-50" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">Unsere Leistungen</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Professionelle Notdienst-Leistungen für alle Notfälle – schnell, zuverlässig und fachgerecht
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Link
                  key={index}
                  to={`/leistungen/${service.slug}`}
                  className="service-card bg-white p-6 rounded-xl"
                  data-testid={`service-card-${index}`}
                >
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#0066cc] rounded-lg flex items-center justify-center">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
                      <p className="text-gray-600">{service.description}</p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Coverage Areas Section */}
      <section className="py-20 bg-white" data-testid="coverage-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">Unser Einsatzgebiet</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Wir sind im Raum Dortmund und Umgebung (ca. 40 km Radius) für Sie im Einsatz
            </p>
          </div>
          
          {cities.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {cities.map((city, index) => (
                <Link
                  key={index}
                  to={`/einsatzgebiete/${city.slug}`}
                  className="location-card bg-gray-50 p-6 rounded-xl"
                  data-testid={`city-card-${index}`}
                >
                  <div className="flex items-center space-x-3 mb-3">
                    <MapPin className="w-6 h-6 text-[#0066cc]" />
                    <h3 className="text-xl font-bold text-gray-900">{city.name}</h3>
                  </div>
                  <p className="text-gray-600 mb-3">{city.description}</p>
                  <p className="text-sm text-[#0066cc] font-semibold">Mehr erfahren →</p>
                </Link>
              ))}
            </div>
          )}
          
          <div className="text-center">
            <Link to="/einsatzgebiete" data-testid="view-all-cities-button">
              <Button size="lg" variant="outline" className="border-2 border-[#0066cc] text-[#0066cc] hover:bg-[#0066cc] hover:text-white font-semibold px-8 rounded-full">
                Alle Einsatzgebiete anzeigen
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">Häufig gestellte Fragen</h2>
            <p className="text-lg text-gray-600">
              Antworten auf die wichtigsten Fragen zu unserem Notdienst
            </p>
          </div>
          
          <Accordion type="single" collapsible className="w-full space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-white rounded-lg px-6 border border-gray-200"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-[#0066cc] py-4">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-[#0066cc] text-white" data-testid="final-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold mb-6">Benötigen Sie sofortige Hilfe?</h2>
          <p className="text-xl mb-8 opacity-90">
            Unser Team steht Ihnen rund um die Uhr zur Verfügung. Rufen Sie jetzt an!
          </p>
          <a href="tel:+4923139083687" data-testid="final-cta-button">
            <Button size="lg" className="bg-[#e63946] hover:bg-[#d62828] text-white text-xl px-10 py-7 rounded-full font-bold shadow-xl">
              <Phone className="w-7 h-7 mr-3" />
              0231 39083687
            </Button>
          </a>
          <p className="mt-6 text-sm opacity-75">Kostenlose Erstberatung – Transparente Abläufe</p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;